// `_id` is an ObjectId; stringify once here so every consumer compares plain
// strings, the same way industries and jobs are normalised below.
const toClient = (client) => ({
  ...client,
  clientId: String(client._id),
  industries: (client.industries || []).map((entry) => ({
    industryId: String(entry.industryId),
    documentation: entry.documentation || [],
  })),
  contacts: client.contacts || [],
  workplaceAddress: client.workplaceAddress || [],
  // { type: "percentage" | "fixed", value } — always present, so a client
  // saved before commission existed reads the same as one with none set.
  commission: {
    type: client.commission?.type || null,
    value: client.commission?.value ?? null,
  },
});

export default {
  // GET Requests //

  // Gets every industry. Reference data rather than per-company, so it is
  // fetched once and reused — the timestamp lets a caller skip a refetch.
  async getIndustries(context) {
    const token = context.rootGetters["authData/token"];
    let response;
    let httpStatus;

    try {
      const res = await fetch(
        process.env.VUE_APP_NODE_SERVER + "/recruitment-industries/retrieve-all",
        {
          method: "GET",
          headers: { Authorization: "Bearer " + token },
        },
      );
      httpStatus = res.status;
      response = await res.json();
    } catch (err) {
      return { status: 500 };
    }

    if (httpStatus !== 200) {
      return { status: httpStatus };
    }

    // The endpoint wraps the list: { message, industries: [...] }
    context.commit(
      "industries",
      (response?.industries || []).map((industry) => ({
        // `_id` is an ObjectId; stringify once here so every consumer
        // compares plain strings.
        industryId: String(industry._id),
        industryName: industry.industryName,
        documentation: industry.documentation || [],
        skills: industry.skills || [],
        roles: industry.roles || [],
        specialisation: industry.specialisation || [],
        // The master list behind jobPreferences.qualifications (FR-22/FR-23).
        // The endpoint has returned it since the projection was widened; it
        // was simply not carried through here, so the value never reached the
        // store and anything offering qualifications had to fall back to free
        // text.
        qualifications: industry.qualifications || [],
      })),
    );
    context.commit("industriesTimestamp");

    return { status: 200 };
  },

  // Gets the job posts belonging to the signed-in user's company (FR-3).
  // The endpoint is paginated and caps pageSize at 100, so this fetches the
  // first page only — enough for the Marketplace list until the job count
  // makes paging worthwhile.
  async getJobs(context) {
    const token = context.rootGetters["authData/token"];
    const companyId = context.rootGetters["userData/companyId"];
    let response;
    let httpStatus;

    if (!companyId) {
      return { status: 400 };
    }

    context.commit("jobsLoading", true);

    try {
      const res = await fetch(
        process.env.VUE_APP_NODE_SERVER +
          "/recruitment-jobs/retrieve-by-company/" +
          companyId +
          "?pageSize=100",
        {
          method: "GET",
          headers: { Authorization: "Bearer " + token },
        },
      );
      httpStatus = res.status;
      response = await res.json();
    } catch (err) {
      context.commit("jobsLoading", false);
      return { status: 500 };
    }

    if (httpStatus !== 200) {
      context.commit("jobsLoading", false);
      return { status: httpStatus };
    }

    // The endpoint wraps the list: { message, jobs: [...], page, total, ... }
    context.commit(
      "jobs",
      (response?.jobs || []).map((job) => ({
        ...job,
        // `_id` is an ObjectId; stringify once here so every consumer
        // compares plain strings.
        jobId: String(job._id),
        industryId: job.industryId ? String(job.industryId) : null,
        roles: job.roles || [],
        specialisation: job.specialisation || [],
        skills: job.skills || [],
        applicants: job.applicants || [],
      })),
    );
    context.commit("jobsTimestamp");
    context.commit("jobsLoading", false);

    return { status: 200 };
  },

  // Reset jobs timestamp
  jobsTimestampReset(context) {
    context.commit("jobsTimestampReset");
  },

  // Gets the clients this company recruits for. Reference data for the Create
  // Job Post form's client selector, so it is fetched alongside the job list.
  async getClients(context) {
    const token = context.rootGetters["authData/token"];
    const companyId = context.rootGetters["userData/companyId"];
    let response;
    let httpStatus;

    if (!companyId) {
      return { status: 400 };
    }

    try {
      const res = await fetch(
        process.env.VUE_APP_NODE_SERVER +
          "/clients/retrieve-by-company/" +
          companyId,
        {
          method: "GET",
          headers: { Authorization: "Bearer " + token },
        },
      );
      httpStatus = res.status;
      response = await res.json();
    } catch (err) {
      return { status: 500 };
    }

    if (httpStatus !== 200) {
      return { status: httpStatus };
    }

    // The endpoint wraps the list: { message, clients: [...] }
    context.commit("clients", (response?.clients || []).map(toClient));
    context.commit("clientsTimestamp");

    return { status: 200 };
  },

  // POST Requests //

  // Creates a client from the Create Job Post form's 'Add New' dialog. The new
  // client is pushed into state so the dropdown can select it immediately.
  async createClient(context, payload) {
    const token = context.rootGetters["authData/token"];
    const companyId = context.rootGetters["userData/companyId"];
    let response;
    let httpStatus;

    try {
      const res = await fetch(
        process.env.VUE_APP_NODE_SERVER + "/clients/create",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          body: JSON.stringify({
            companyId: companyId,
            companyName: payload.companyName,
            phoneNumber: payload.phoneNumber || null,
            industries: payload.industries || [],
            contacts: payload.contacts || [],
            workplaceAddress: payload.workplaceAddress || [],
            commission: payload.commission || null,
          }),
        },
      );
      httpStatus = res.status;
      response = await res.json();
    } catch (err) {
      return { status: 500 };
    }

    if (httpStatus !== 201) {
      return { status: httpStatus };
    }

    const client = toClient(response.client);
    context.commit("clientAdd", client);

    return { status: 201, client: client };
  },

  // Updates a client. The server changes only the fields it is sent, so the
  // caller passes just what it edited — contacts and workplaceAddress go as
  // whole lists, commission as { type, value } (or null to clear it).
  async patchClient(context, payload) {
    const token = context.rootGetters["authData/token"];
    const companyId = context.rootGetters["userData/companyId"];
    const { clientId, ...fields } = payload;
    let response;
    let httpStatus;

    try {
      const res = await fetch(
        process.env.VUE_APP_NODE_SERVER + "/clients/update",
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          body: JSON.stringify({
            companyId: companyId,
            clientId: clientId,
            ...fields,
          }),
        },
      );
      httpStatus = res.status;
      response = await res.json();
    } catch (err) {
      return { status: 500 };
    }

    if (httpStatus !== 200) {
      // A rejected field (e.g. a commission over 100%) comes back in `errors`
      return {
        status: httpStatus,
        message: response?.message || response?.errors?.[0]?.msg,
      };
    }

    const client = toClient(response.client);
    context.commit("clientUpdate", client);

    return { status: 200, client: client };
  },

  // Deletes a client. Jobs that named it keep their link; the job list falls
  // back to the free-text company name, or Internal, once it is gone.
  async deleteClient(context, payload) {
    const token = context.rootGetters["authData/token"];
    const companyId = context.rootGetters["userData/companyId"];
    let httpStatus;

    try {
      const res = await fetch(
        process.env.VUE_APP_NODE_SERVER + "/clients/remove",
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          body: JSON.stringify({
            companyId: companyId,
            clientId: payload.clientId,
          }),
        },
      );
      httpStatus = res.status;
    } catch (err) {
      return { status: 500 };
    }

    // The endpoint answers a successful delete with 201.
    if (httpStatus !== 201) {
      return { status: httpStatus };
    }

    context.commit("clientRemove", { clientId: payload.clientId });

    return { status: 201 };
  },

  // Moves a candidate to a new stage and/or sub-status (FR-18, FR-18a). Used by
  // the Candidates board for both a drag between columns and a sub-status pick.
  async patchApplicant(context, payload) {
    const token = context.rootGetters["authData/token"];
    const companyId = context.rootGetters["userData/companyId"];
    let response;
    let httpStatus;

    try {
      const res = await fetch(
        process.env.VUE_APP_NODE_SERVER + "/recruitment-applicants/update",
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          body: JSON.stringify({
            // Required by checkRecruitmentEnabled (FR-39), which resolves the
            // owning company from the body on this route — `jobId` only helps
            // it when supplied as a URL parameter, which this endpoint has not.
            companyId: companyId,
            // The signed-in user's own id. An approval records it as the
            // contract's recruiter; without it the server falls back to an id
            // derived from the Auth0 subject.
            actingUserId: context.rootGetters["userData/userId"],
            jobId: payload.jobId,
            applicantId: payload.applicantId,
            stage: payload.stage,
            // Omitted entirely when the caller has no opinion, so the server
            // can apply the new stage's default. Sending null would be an
            // explicit "clear it" and would defeat that.
            ...(payload.stageStatus !== undefined
              ? { stageStatus: payload.stageStatus }
              : {}),
            // The terms this applicant is after, where they differ from the
            // job's own (FR-8 of §3.2). Omitted unless the caller supplied
            // it — the server merges field by field, so sending a partial
            // object (e.g. { hoursPerWeek: 38 }, or null to clear it)
            // leaves everything else on the record alone.
            ...(payload.contract !== undefined
              ? { contract: payload.contract }
              : {}),
            // false approves without telling the candidate (Offer Outcome).
            ...(payload.notify !== undefined ? { notify: payload.notify } : {}),
          }),
        },
      );
      httpStatus = res.status;
      response = await res.json();
    } catch (err) {
      return { status: 500 };
    }

    if (httpStatus !== 200) {
      // A rejected field (e.g. hoursPerWeek over 168) comes back in `errors`
      return {
        status: httpStatus,
        message: response?.message || response?.errors?.[0]?.msg,
      };
    }

    context.commit("applicantUpdate", {
      jobId: payload.jobId,
      applicant: response.applicant,
    });

    // This approval filled the job: the server fulfilled it and rejected
    // everyone else still in the pipeline, so the whole list is reloaded.
    if (response.jobFulfilled) {
      await context.dispatch("getJobs");
    }

    return {
      status: 200,
      applicant: response.applicant,
      jobFulfilled: response.jobFulfilled === true,
    };
  },

  // Adds an existing platform user to a job as a candidate. JSON, not
  // multipart: there is no file and nothing about the person to send — the
  // record is the link and the stage, and everything displayed about them is
  // read from the company's user list.
  async postApplicant(context, payload) {
    const token = context.rootGetters["authData/token"];
    const companyId = context.rootGetters["userData/companyId"];
    let response;
    let httpStatus;

    try {
      const res = await fetch(
        process.env.VUE_APP_NODE_SERVER + "/recruitment-applicants/add",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          body: JSON.stringify({
            // Required by checkRecruitmentEnabled (FR-39), which resolves the
            // owning company from the body on this route.
            companyId: companyId,
            jobId: payload.jobId,
            userId: payload.userId,
          }),
        },
      );
      httpStatus = res.status;
      response = await res.json();
    } catch (err) {
      return { status: 500 };
    }

    if (httpStatus !== 201) {
      return { status: httpStatus, message: response?.message };
    }

    context.commit("applicantAdd", {
      jobId: payload.jobId,
      applicant: response.applicant,
    });

    return { status: 201, applicant: response.applicant };
  },

  // A general user applying to a job themselves (US-22). Not `/add`, which is
  // a recruiter adding someone and needs `update:recruitment`; this takes the
  // `read:recruitment` scope the marketplace reads with. The new applicant is
  // pushed onto the job so the page shows "Applied" without a refetch.
  async applyToJob(context, payload) {
    const token = context.rootGetters["authData/token"];
    const companyId = context.rootGetters["userData/companyId"];
    const userId = context.rootGetters["userData/userId"];
    let response;
    let httpStatus;

    try {
      const res = await fetch(
        process.env.VUE_APP_NODE_SERVER + "/recruitment-applicants/apply",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          body: JSON.stringify({
            jobId: payload.jobId,
            companyId: companyId,
            userId: userId,
          }),
        },
      );
      httpStatus = res.status;
      response = await res.json();
    } catch (err) {
      return { status: 500 };
    }

    if (httpStatus !== 201) {
      // 409 carries why: `duplicate_applicant` or `job_closed`.
      return { status: httpStatus, error: response?.error || null };
    }

    context.commit("applicantAdd", {
      jobId: payload.jobId,
      applicant: response.applicant,
    });

    return { status: 201, applicant: response.applicant };
  },

  // FR-11: records that the current user has opened a job. Idempotent on the
  // server ($addToSet), so the caller only skips it to save a round trip.
  async markJobViewed(context, payload) {
    const token = context.rootGetters["authData/token"];
    const companyId = context.rootGetters["userData/companyId"];
    const userId = context.rootGetters["userData/userId"];
    let response;
    let httpStatus;

    try {
      const res = await fetch(
        process.env.VUE_APP_NODE_SERVER + "/recruitment-jobs/mark-viewed",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          body: JSON.stringify({
            jobId: payload.jobId,
            companyId: companyId,
            userId: userId,
          }),
        },
      );
      httpStatus = res.status;
      response = await res.json();
    } catch (err) {
      return { status: 500 };
    }

    if (httpStatus !== 200) {
      return { status: httpStatus };
    }

    context.commit("jobViewed", {
      jobId: payload.jobId,
      viewed: response.viewed,
    });

    return { status: 200 };
  },

  // Packages candidates on a job for the client and downloads the zip
  // (design/pdf_creator). The server also saves each candidate's terms, so the
  // caller refetches the job afterwards. Nothing is
  // committed to the store here: the response is a file, not a record.
  async packageSubmission(context, payload) {
    const token = context.rootGetters["authData/token"];
    const companyId = context.rootGetters["userData/companyId"];
    let res;

    try {
      res = await fetch(
        process.env.VUE_APP_NODE_SERVER +
          "/recruitment-applicants/package-submission",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          body: JSON.stringify({
            companyId: companyId,
            jobId: payload.jobId,
            candidates: payload.candidates,
          }),
        },
      );
    } catch (err) {
      return { status: 500 };
    }

    if (res.status !== 200) {
      let response;
      try {
        response = await res.json();
      } catch (err) {
        response = null;
      }
      return {
        status: res.status,
        message: response?.message,
        errors: response?.errors,
      };
    }

    // The server names the file (`<Job_Title>_Submission_<date>.zip`) in
    // Content-Disposition, which it exposes to the browser for this response.
    const disposition = res.headers.get("Content-Disposition") || "";
    const match = disposition.match(/filename="?([^";]+)"?/i);
    const fileName = match ? match[1] : "Submission.zip";

    try {
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      return { status: 500 };
    }

    return { status: 200, fileName: fileName };
  },

  // Posts a note against an applicant on a job (FR-20). Notes accumulate
  // server-side — the endpoint appends rather than replaces.
  async postApplicantNote(context, payload) {
    const token = context.rootGetters["authData/token"];
    const companyId = context.rootGetters["userData/companyId"];
    const userId = context.rootGetters["userData/userId"];
    let response;
    let httpStatus;

    try {
      const res = await fetch(
        process.env.VUE_APP_NODE_SERVER + "/recruitment-applicants/update",
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          body: JSON.stringify({
            companyId: companyId,
            // The signed-in user's own id, the one the database stores, so the
            // note names somebody the company list can resolve. Without it the
            // server falls back to an id derived from the Auth0 subject, which
            // agrees only for identities it created itself.
            actingUserId: context.rootGetters["userData/userId"],
            jobId: payload.jobId,
            applicantId: payload.applicantId,
            notes: [
              {
                description: payload.description,
                // Sent explicitly rather than left to the server: it derives
                // the author from the Auth0 subject, which only resolves to an
                // app userId for accounts created through the app. Without
                // this the author would not be found and would render as a
                // previous employee.
                userId: userId,
              },
            ],
          }),
        },
      );
      httpStatus = res.status;
      response = await res.json();
    } catch (err) {
      return { status: 500 };
    }

    if (httpStatus !== 200) {
      return { status: httpStatus, message: response?.message };
    }

    context.commit("applicantUpdate", {
      jobId: payload.jobId,
      applicant: response.applicant,
    });

    return { status: 200, applicant: response.applicant };
  },

  // Creates a job post (FR-4).
  // Updates an existing job. Same payload as createJob plus the jobId — the
  // backend treats every field as optional, so sending the whole form keeps
  // this symmetric with create rather than needing a diff.
  async patchJob(context, payload) {
    const token = context.rootGetters["authData/token"];
    const companyId = context.rootGetters["userData/companyId"];
    let response;
    let httpStatus;

    try {
      const res = await fetch(
        process.env.VUE_APP_NODE_SERVER + "/recruitment-jobs/update",
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          body: JSON.stringify({ ...payload, companyId: companyId }),
        },
      );
      httpStatus = res.status;
      response = await res.json();
    } catch (err) {
      return { status: 500 };
    }

    if (httpStatus !== 200) {
      return { status: httpStatus, errors: response?.errors };
    }

    // The list is now stale — the next visit to Job Posts refetches it.
    context.commit("jobsTimestampReset");

    return { status: 200, job: response.job };
  },
  async createJob(context, payload) {
    const token = context.rootGetters["authData/token"];
    const companyId = context.rootGetters["userData/companyId"];
    let response;
    let httpStatus;

    try {
      const res = await fetch(
        process.env.VUE_APP_NODE_SERVER + "/recruitment-jobs/create",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          body: JSON.stringify({ ...payload, companyId: companyId }),
        },
      );
      httpStatus = res.status;
      response = await res.json();
    } catch (err) {
      return { status: 500 };
    }

    if (httpStatus !== 201) {
      return { status: httpStatus, errors: response?.errors };
    }

    // The list is now stale — the next visit to Job Posts refetches it.
    context.commit("jobsTimestampReset");

    return { status: 201, job: response.job };
  },
};
